import React, { useEffect, useMemo, useState } from 'react';
import './TutorialDashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faUtensils, faThList, faImage, faAddressBook, faClock, faUser, faQrcode,
    faClipboardList, faChartLine, faCashRegister, faCode, faChair, faUsersCog, faStore,
    faArrowLeft, faArrowRight, faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import { usePlan } from '../PlanContext';
import baseURL from '../url';
import storage from '../storage';

const pasosBase = [
    {
        icon: faHome,
        titulo: 'Inicio',
        texto: 'Resumen de tu negocio: productos, categorías, banners y usuarios en un solo lugar.',
    },
    {
        icon: faUtensils,
        titulo: 'Productos',
        texto: 'Registrá y editá tus productos: título, precio, descripción, imágenes y stock.',
    },
    {
        icon: faThList,
        titulo: 'Categorías',
        texto: 'Organizá tus productos en categorías para que tus clientes encuentren fácil lo que buscan.',
    },
    {
        icon: faImage,
        titulo: 'Banners',
        texto: 'Agregá imágenes destacadas a la portada de tu catálogo para llamar la atención.',
    },
    {
        icon: faAddressBook,
        titulo: 'Contacto',
        texto: 'Configurá el logo, el nombre de tu negocio, redes sociales, WhatsApp, email y dirección.',
    },
    {
        icon: faClock,
        titulo: 'Horarios',
        texto: 'Definí horarios de apertura y cierre. Tu tienda muestra automáticamente si está abierta o cerrada.',
    },
    {
        icon: faUser,
        titulo: 'Usuarios',
        texto: 'Administrá los usuarios que tienen acceso a tu panel de administración.',
    },
    {
        icon: faQrcode,
        titulo: 'Generador QR',
        texto: 'Obtené el enlace y el código QR de tu tienda para compartirlo con tus clientes.',
    },
];

const pasosPlus = [
    {
        icon: faClipboardList,
        titulo: 'Pedidos',
        texto: 'Seguí y gestioná los pedidos que llegan a través de tu tienda en línea.',
    },
    {
        icon: faChartLine,
        titulo: 'Reportes',
        texto: 'Consultá estadísticas de ventas y el rendimiento de tu negocio.',
    },
    {
        icon: faCashRegister,
        titulo: 'Gestión de Cajas',
        texto: 'Abrí y controlá las cajas del día, y registrá sus movimientos.',
    },
    {
        icon: faCode,
        titulo: 'Códigos',
        texto: 'Creá códigos de descuento para premiar a tus clientes.',
    },
    {
        icon: faChair,
        titulo: 'Mesas',
        texto: 'Administrá las mesas de tu local para el servicio en el lugar.',
    },
    {
        icon: faUsersCog,
        titulo: 'Trabajadores',
        texto: 'Gestioná tu equipo y los permisos de acceso de cada trabajador.',
    },
    {
        icon: faStore,
        titulo: 'Sucursales',
        texto: 'Administrá las sucursales de tu negocio y sus datos de contacto.',
    },
];

export default function TutorialDashboard() {
    const { isPlus, esTienda, loading: planLoading } = usePlan();
    const [pasoActual, setPasoActual] = useState(0);
    const [cargado, setCargado] = useState(false);
    const [productosVacios, setProductosVacios] = useState(false);
    const [cerrado, setCerrado] = useState(false);

    useEffect(() => {
        fetch(`${baseURL}/productosGet.php`)
            .then(r => r.json())
            .then(data => setProductosVacios((data.productos || []).length === 0))
            .catch(() => setProductosVacios(true))
            .finally(() => setCargado(true));
    }, []);

    const pasos = useMemo(() => {
        const lista = [...pasosBase];
        if (isPlus) {
            pasosPlus.forEach(paso => {
                if (paso.titulo === 'Mesas' && esTienda) return;
                lista.push(paso);
            });
        }
        return lista;
    }, [isPlus, esTienda]);

    const mostradoAntes = !!storage.getItem('tutorialVisto');

    if (!cargado || planLoading || cerrado || mostradoAntes || !productosVacios) return null;

    const esUltimo = pasoActual === pasos.length - 1;

    const terminar = () => {
        storage.setItem('tutorialVisto', '1');
        setCerrado(true);
    };

    const siguiente = () => {
        if (esUltimo) terminar();
        else setPasoActual(pasoActual + 1);
    };

    const anterior = () => {
        if (pasoActual > 0) setPasoActual(pasoActual - 1);
    };

    const salir = () => {
        terminar();
    };

    const paso = pasos[pasoActual];

    return (
        <div className="tutorialOverlay">
            <div className="tutorialModal">
                <button className="tutorialCerrar" onClick={salir} title="Saltar tutorial">
                    &times;
                </button>

                <div className="tutorialBadge">
                    <FontAwesomeIcon icon={faGraduationCap} />
                    <span>Guía rápida</span>
                </div>

                <div className="tutorialIcono" style={{ background: `linear-gradient(135deg, #7c3aed, #4f46e5)` }}>
                    <FontAwesomeIcon icon={paso.icon} />
                </div>

                <h2>{paso.titulo}</h2>
                <p className="tutorialTexto">{paso.texto}</p>

                <div className="tutorialDots">
                    {pasos.map((_, index) => (
                        <span
                            key={index}
                            className={`tutorialDot ${index === pasoActual ? 'activo' : ''}`}
                            onClick={() => setPasoActual(index)}
                        />
                    ))}
                </div>

                <div className="tutorialBtns">
                    <button className="tutorialBtnSec" onClick={anterior} disabled={pasoActual === 0}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Anterior
                    </button>
                    <button className="tutorialBtnPrim" onClick={siguiente}>
                        {esUltimo ? '¡Empezar!' : <>Siguiente <FontAwesomeIcon icon={faArrowRight} /></>}
                    </button>
                </div>
            </div>
        </div>
    );
}